import { useState } from 'react';

import { Checkbox } from '@apps/web/components/ui/checkbox';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { isTransparentRateLimitError } from '@apps/web/lib/known_api_errors';
import { type OrgCaregiverRecord } from '@libraries/typescript/api_client/schema_types';

type ContactChannel = NonNullable<
    OrgCaregiverRecord['contact_channels']
>[number];

const CHANNEL_OPTIONS: ReadonlyArray<{
    readonly value: ContactChannel;
    readonly label: string;
}> = [
    { value: 'sms', label: 'Texts' },
    { value: 'voice', label: 'Calls' },
];

type CaregiverContactChannelsProps = {
    readonly organizationId: string;
    readonly caregiverId: string;
    readonly contactChannels: ReadonlyArray<ContactChannel>;
    readonly onSaved: () => void;
};

/**
 * Which channels shift outreach may use for this caregiver. Outreach steps on
 * an unchecked channel are skipped for them. Each toggle saves immediately;
 * the last checked channel cannot be unchecked because the caregiver must stay
 * reachable (use the outreach skip list to stop contacting them entirely).
 */
export function CaregiverContactChannels({
    organizationId,
    caregiverId,
    contactChannels,
    onSaved,
}: CaregiverContactChannelsProps) {
    const [isSaving, setIsSaving] = useState(false);

    const handleToggle = async (channel: ContactChannel, checked: boolean) => {
        const nextChannels = CHANNEL_OPTIONS.map(
            (option) => option.value,
        ).filter((value) =>
            value === channel ? checked : contactChannels.includes(value),
        );
        setIsSaving(true);
        try {
            await getApiClient().patch({
                url: '/organizations/{organization_id}/caregivers/{caregiver_id}',
                pathParams: {
                    organization_id: organizationId,
                    caregiver_id: caregiverId,
                },
                body: { contact_channels: nextChannels },
            });
            onSaved();
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                toast.error('Failed to update outreach channels.', {
                    persistNotification: true,
                    notificationType: 'form_failure',
                });
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="min-w-0">
            <div className="text-muted-foreground mb-1 flex h-4 items-center text-[10px] leading-none">
                Shift outreach can use
            </div>
            <div className="flex min-h-8 items-center gap-4">
                {CHANNEL_OPTIONS.map((option) => {
                    const checked = contactChannels.includes(option.value);
                    const isOnlyChannel =
                        checked && contactChannels.length === 1;
                    return (
                        <label
                            key={option.value}
                            htmlFor={`contact-channel-${option.value}`}
                            className="flex items-center gap-1.5 text-xs"
                        >
                            <Checkbox
                                id={`contact-channel-${option.value}`}
                                checked={checked}
                                disabled={isSaving || isOnlyChannel}
                                onCheckedChange={(value) =>
                                    handleToggle(option.value, value === true)
                                }
                            />
                            {option.label}
                        </label>
                    );
                })}
            </div>
        </div>
    );
}
